
// @TODO: Everything related to reviews should be moved outside the page

import { Component, namespace, Vue } from 'nuxt-property-decorator'
import type { ProviderModel, RatingStatModel } from '@alao-frontend/erp-services'
import { Context } from '@nuxt/types'
import type { MetaInfo } from 'vue-meta'
import { OnCreated, OnMounted, WithAsyncData, WithHead } from '~/core/vue.types'
import { OfferType, OfferTypeByShortName, OfferTypeKey, ExtendedProviderModel, Review } from '~/core/offer.types'
import ProviderLayout from '~/views/providers/ProviderLayout.vue'
import ProviderCommonCard from '~/components/providers/ProviderCommonCard.vue'
import AboutProvider from '~/components/providers/AboutProvider.vue'
import ProviderContentArticle from '~/components/providers/ProviderContentArticle.vue'
import CheckAvailabilitySection from '~/components/providers/CheckAvailabilitySection.vue'
import ReviewsSection from '~/components/sections/rating/ReviewsSection.vue'
import ReviewsFilter from '~/components/sections/rating/ReviewsFilter.vue'
import ReviewsBox from '~/components/sections/rating/ReviewsBox.vue'
import ProviderRatingCarousel from '~/components/providers/ProviderRatingCarousel.vue'
import ProviderRatingCard from '~/components/providers/ProviderRatingCard.vue'
import { $api } from '~/plugins/axios'
import { namespace as mobileFiltersStore } from '~/store/modules/Amp/Filters/Mobile'
import { namespace as homeFiltersStore } from '~/store/modules/Amp/Filters/Home'
import SaveRouteMixin from '~/mixins/save-route.mixin'
import ProviderComparator from '~/views/comparator/ProviderComparator.vue'
import { useSEOMetaComposer, buildProviderOrganizationJSONLD } from '~/composables/seo.composable'
import { providerWebsiteMap } from '~/core/config/providers'
import { FiltersItemValue, AmpHomeFiltersState, AmpMobileFiltersState } from '~/core/config/filters/types'
import { namespace as baseInfoStore, GetterType as BaseInfoGetterType, BaseInfoState } from '~/store/modules/BaseInfo'

interface AsyncComponentData {
  slug: string,
  provider: ExtendedProviderModel,
  feedbackRatingStats: RatingStatModel[],
  contentArticles: any[],
}

let debounce: any = null

const BaseInfoStore = namespace(baseInfoStore)

const HomeFiltersModule = namespace(mobileFiltersStore)
const MobileFiltersModule = namespace(homeFiltersStore)

@Component({
  nuxtI18n: {
    paths: {
      en: '/provider/:slug',
      de: '/anbieter/:slug',
      fr: '/operateur/:slug',
    },
  },
  components: {
    ProviderComparator,
    ProviderLayout,
    ProviderCommonCard,
    AboutProvider,
    ProviderContentArticle,
    CheckAvailabilitySection,
    ReviewsSection,
    ReviewsFilter,
    ReviewsBox,
    ProviderRatingCarousel,
    ProviderRatingCard,
  },
  mixins: [SaveRouteMixin],
  middleware: (ctx) => {
    if (ctx.params.slug === 'm-budget') {
      ctx.redirect(301, ctx.localePath({
        name: 'provider-slug', params: { slug: 'migros-mobile' },
      }))
    }
  },
})
export default class SpecificProvider extends Vue implements WithAsyncData, OnMounted, OnCreated, WithHead {
  @BaseInfoStore.Getter(BaseInfoGetterType.ACTIVE_PROVIDERS)
  providersList!: BaseInfoState['providers']

  slug!: string
  provider!: ExtendedProviderModel
  contentArticles!: any[]
  sidebarArticles!: any[]
  feedbackRatingStats!: RatingStatModel[]

  private offerType: OfferTypeKey = OfferTypeByShortName.mobile

  private reviewsPage = 1
  private reviewsCount = 0
  private reviewsFilterForm = {
    city: '',
    ageGroup: 0,
    rating: 0,
  }

  private isReviewsFullLoading = true
  private isReviewsPartLoading = false

  protected reviewsList: Review[] = []

  @MobileFiltersModule.Getter('GET_FILTERS')
  readonly mobileFilters!: AmpMobileFiltersState

  @HomeFiltersModule.Getter('GET_FILTERS')
  readonly homeFilters!: AmpHomeFiltersState

  async asyncData (ctx: Context): Promise<AsyncComponentData | void> {
    const {
      redirect,
      localePath,
      params: {
        slug,
      },
    } = ctx

    const provider: ProviderModel | undefined = ctx.store.getters[`${baseInfoStore}/${BaseInfoGetterType.PROVIDER_BY_SLUG}`](slug)

    if (!provider) {
      return redirect(301, localePath({
        name: 'compare-mobile',
      }))
    }

    try {
      const [
        { items: extendedProviders },
        { items: contentArticles },
        feedbackRatingStats,
      ] = await Promise.all([
        $api.PublicHandbookService.searchHandbooks({
          handbook_slug: 'provider',
          page: 1,
          per_page: 1,
          id: provider.id
        }),
        $api.PublicHandbookService.searchProviderArticles({
          provider_id: provider.id,
        }),
        $api.PublicFeedbackService.getFeedbackRatingStats(),
      ])

      if (!extendedProviders) {
        return redirect(301, localePath({
          name: 'compare-mobile',
        }))
      }

      const extendedProvider = extendedProviders.find((p: any) => p.id === provider.id) as ExtendedProviderModel | undefined

      if (!extendedProvider) {
        return redirect(301, localePath({
          name: 'compare-mobile',
        }))
      }

      return {
        slug,
        provider: extendedProvider,
        feedbackRatingStats: feedbackRatingStats ?? [],
        contentArticles: contentArticles ?? [],
      }
    } catch (e:any) {
      redirect(301, localePath({
        name: 'compare-mobile',
      }))
    }
  }

  head (): MetaInfo {
    return useSEOMetaComposer(
      this,
      {
        title: this.seoTitle,
        description: this.seoDescription,
        keywords: this.seoKeywords,
        route: { name: 'provider-slug', params: { slug: this.$route.params.slug } },
        customJSONLD: [this.organizationMarkup, this.aggregateRatingMarkup]
          .filter(Boolean) as Record<string, any>[],
        breadcrumb: { name: this.provider.title },
        isExcludeOrgRating: true,
      },
    )
  }

  get providerScores () {
    return this.feedbackRatingStats.find(item => item.provider_id === this.provider.id) || {} as RatingStatModel
  }

  get providersListWithoutCurrent () {
    return this.providersList.filter((p) => {
      return p.id !== this.provider.id
    })
  }

  get reviewsPerPage () {
    if (!process.browser) {
      return 4
    }

    if (window.matchMedia('(max-width: 575px)').matches) {
      return 1
    }

    if (window.matchMedia('(max-width: 767px)').matches) {
      return 2
    }

    if (window.matchMedia('(max-width: 991px)').matches) {
      return 3
    }

    return 4
  }

  get pageTitle () {
    return this.provider.title_h1 || ''
  }

  get seoTitle () {
    return this.provider.title_SEO || ''
  }

  get seoDescription () {
    return this.provider.meta_description || ''
  }

  get seoKeywords () {
    return this.provider.meta_keywords || ''
  }

  get content () {
    return this.contentArticles.map((article: any) => {
      return {
        ...article,
        slug: article.title.toLowerCase().replace(/[^a-zA-Z]+/g, '-'),
      }
    })
  }

  get tableOfContents () {
    return this.content.map((article: any) => {
      return {
        title: article.title,
        href: `#${article.slug}`,
      }
    })
  }

  get checkAvailabilityLink () {
    return this.localePath({
      name: 'coverage',
      query: {
        provider: this.provider.slug,
      },
    })
  }

  get reviewsPagesAvailable () {
    return Math.ceil(this.reviewsCount / this.reviewsPerPage)
  }

  get providerName () {
    return this.provider.title
  }

  get organizationMarkup () {
    return buildProviderOrganizationJSONLD({
      name: this.providerName,
      url: this.provider.json_ld_markup?.url || providerWebsiteMap[this.provider.slug],
      logoUrl: this.provider.logo?.w200,
      description: this.provider.meta_description || undefined,
    })
  }

  get aggregateRatingMarkup (): Record<string, any> | null {
    const { rating, reviews_count: reviewsCount } = this.provider

    if (!rating || !reviewsCount) {
      return null
    }

    return {
      '@context': 'https://schema.org',
      '@type': 'AggregateRating',
      itemReviewed: {
        '@type': 'Organization',
        name: this.providerName,
      },
      ratingValue: String(rating),
      reviewCount: String(reviewsCount),
      bestRating: '5',
      worstRating: '1',
    }
  }

  onRatingCarouselCTAClick (slug: string) {
    this.$router.push(this.localePath({
      name: 'provider-slug',
      params: {
        slug,
      },
    }))
  }

  setOfferType (offerType: OfferTypeKey) {
    this.offerType = offerType
    this.$router.push({ path: this.$route.path, query: { offer_type: offerType } })
  }

  scrollTo (hash: string) {
    const element = document.querySelector(hash)

    if (!element) {
      return
    }

    this.$scrollTo(element, 200, {
      offset: -80,
    })
  }

  onPaginationChange (page: number) {
    this.reviewsPage = page
    this.fetchReviewsList()
  }

  onReviewsFilterCityChange (val: string) {
    this.reviewsFilterForm.city = val
    this.resetPage()
    this.debouncedFetchReviews()
  }

  onReviewsFilterAgeGroupChange (val: number) {
    this.reviewsFilterForm.ageGroup = val
    this.resetPage()
    this.debouncedFetchReviews()
  }

  onReviewsFilterRatingChange (val: number) {
    this.reviewsFilterForm.rating = val
    this.resetPage()
    this.debouncedFetchReviews()
  }

  resetPage () {
    this.reviewsPage = 1
  }

  debouncedFetchReviews () {
    if (debounce) {
      clearTimeout(debounce)
    }
    debounce = setTimeout(this.fetchReviewsList, 400)
  }

  async fetchReviewsList () {
    let loaderDebounce: any = null

    try {
      if (this.reviewsPage === 1) {
        // Prevent flicker
        loaderDebounce = setTimeout(() => {
          this.isReviewsFullLoading = true
        }, 400)
      } else {
        this.isReviewsPartLoading = true
      }

      const { city, rating, ageGroup } = this.reviewsFilterForm
      const params = {
        provider_id: this.provider.id,
        per_page: this.reviewsPerPage,
        page: this.reviewsPage,
        rating,
      } as Record<string, unknown>
      if (city) {
        params.city = city
      }
      if (ageGroup !== 0) {
        params.age_group = ageGroup
      }
      const { items: results, pagination: { total: count } } = await $api.RatingService.getReviewsList(params)
      this.reviewsList = results
      this.reviewsCount = count
    } catch (e) {
      // TODO: Handle error
      // eslint-disable-next-line no-console
      console.error(e)
    } finally {
      if (loaderDebounce) {
        clearTimeout(loaderDebounce)
      }

      this.isReviewsFullLoading = false
      this.isReviewsPartLoading = false
    }
  }

  created () {
    const offerType = this.$route.query.offer_type as string

    if (offerType && offerType in OfferType) {
      this.setOfferType(offerType as OfferTypeByShortName)
    }
  }

  mounted () {
    this.$analytics.GTM.sendPageViewEvent({
      routeName: this.$route.name as string,
      pageTitle: this.$metaInfo.title || '',
      fullPath: this.$route.fullPath,
      fromPath: this.$nuxt.context.from ? this.$nuxt.context.from.fullPath : '',
      contentGroup: 'Plan Provider',
    })

    this.fetchReviewsList()
  }

  @MobileFiltersModule.Mutation('SET_FILTER')
  setMobileilters!: ({
    value,
    key,
  }: { value: FiltersItemValue, key: string }) => void

  @HomeFiltersModule.Mutation('SET_FILTER')
  setHomeFilters!: ({
    value,
    key,
  }: { value: FiltersItemValue, key: string }) => void

  destroyed () {
    this.setMobileilters({
      key: 'providers',
      value: [],
    })
    this.setHomeFilters({
      key: 'providers',
      value: [],
    })
  }
}
